import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { intervalToDuration } from 'date-fns';
import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';

import { UILoader } from '@futrli/ui-components';

import styles from './latest-version-refresh-modal.scss';

export interface LatestVersionRefreshModalProps {
  onReload?: () => void;
  onSnooze?: () => void;
  show?: boolean;
  timerValue?: number;
}

const DEFAULT_TIMER_VALUE_MS = 60000;

export const LatestVersionRefreshModal = ({
  onReload,
  onSnooze,
  show,
  timerValue = DEFAULT_TIMER_VALUE_MS
}: LatestVersionRefreshModalProps) => {
  const intl = useIntl();
  const timeLeft = intervalToDuration({ start: 0, end: timerValue });
  const minutesLeftString = timeLeft?.minutes ? `${timeLeft.minutes}m ` : '';
  const secondsLeftString = timeLeft?.seconds ? `${timeLeft.seconds}s` : '';
  const isReloading = timerValue <= 0;

  return (
    <Dialog
      open={!!show}
      className={styles.modal}
      onCancel={onSnooze}
      size="medium-small"
      aria-label={intl.formatMessage({ id: 'skeleton.new_version_detected', defaultMessage: 'New version detected' })}
      title={intl.formatMessage({ id: 'skeleton.new_version_detected', defaultMessage: 'New version detected' })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          <Button disabled={isReloading} onClick={onSnooze} data-testid="dialog-actions-secondary">
            <FormattedMessage id="skeleton.snooze" defaultMessage="Snooze" />
          </Button>
        }
        saveButton={
          <Button disabled={isReloading} onClick={onReload} buttonType="primary" data-testid="dialog-actions-primary">
            <FormattedMessage id="skeleton.reload" defaultMessage="Reload" />
          </Button>
        }
        onSubmit={e => e.preventDefault()}
      >
        {isReloading ? (
          <p className={styles.reloading}>
            <UILoader />
            <FormattedMessage
              id="skeleton.reloading_now"
              defaultMessage="Predict will reload in a few moments, please wait..."
            />
          </p>
        ) : (
          <>
            <p>
              <FormattedMessage
                id="skeleton.new_version_explaination"
                defaultMessage="A new version of Predict is available. The page will automatically reload in {time}, or you can snooze to delay for 30 minutes."
                values={{ time: `${minutesLeftString}${secondsLeftString}`.trim() }}
              />
            </p>
          </>
        )}
      </Form>
    </Dialog>
  );
};
